import { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../BaseComponents/Modal';
import DatePickerComp from '../BaseComponents/DatePicker';
import Select, { SelectOption } from '../BaseComponents/Select';
import MultiSelect from '../BaseComponents/MultiSelect';
import TextArea from '../BaseComponents/TextArea';
import Button from '../BaseComponents/Button';
import './FutureTestsModal.scss';
import {
  createFutureTest,
  FutureTestItem,
  updateFutureTest,
} from '../../api/future-test';
import { getPanelsList, PanelWithDetails } from '../../api/panels';
import {
  FUTURE_TEST_MODALITY_OPTIONS,
  MRI_BODY_PART_OPTIONS,
  normalizeFutureTestModalityKey,
} from '../../utils/constants';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (created: FutureTestItem) => void;
  initialTest?: FutureTestItem | null;
  memberId?: string;
};

const BLOODS_MODALITY = 'BLOODS';
const MRI_MODALITY = 'MRI';

const toggleMultiSelectOption = (
  current: SelectOption[],
  option: SelectOption
): SelectOption[] => {
  const existingIndex = current.findIndex((item) => item.key === option.key);
  if (existingIndex >= 0) {
    return current.filter((_, index) => index !== existingIndex);
  }
  return [...current, option];
};

const FutureTestsModal = ({
  isOpen,
  onClose,
  onSubmit,
  memberId,
  initialTest
}: Props) => {
  const [panelOptions, setPanelOptions] = useState<SelectOption[]>([]);
  const [, setIsLoadingPanels] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isMonthOnly, setIsMonthOnly] = useState<boolean>(false);
  const [selectedModality, setSelectedModality] = useState<SelectOption | null>(null);
  const [selectedPanels, setSelectedPanels] = useState<SelectOption[]>([]);
  const [selectedBodyParts, setSelectedBodyParts] = useState<SelectOption[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);

  const isBloodsSelected = selectedModality?.key === BLOODS_MODALITY;
  const isMriSelected = selectedModality?.key === MRI_MODALITY;

  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(null);
      setIsMonthOnly(false);
      setSelectedModality(null);
      setSelectedPanels([]);
      setSelectedBodyParts([]);
      setNotes('');
      setIsSubmitting(false);
      setIsTouched(false);
      return;
    }

    setIsSubmitting(false);
    setIsTouched(false);
    if (!initialTest) {
      setSelectedDate(null);
      setIsMonthOnly(false);
      setSelectedModality(null);
      setSelectedPanels([]);
      setSelectedBodyParts([]);
      setNotes('');
    }

    const loadPanels = async () => {
      if (panelOptions.length > 0) return;
      setIsLoadingPanels(true);
      try {
        const res = await getPanelsList(null, 100, 0);
        const options = res.panels.map((panel: PanelWithDetails) => ({
          key: panel.id,
          value: panel.name
        }));
        setPanelOptions(options);
      } catch (error) {
        console.error('Failed to load panels list', error);
        toast.error('Unable to load panels. Please try again.');
      } finally {
        setIsLoadingPanels(false);
      }
    };

    loadPanels();
  }, [isOpen, panelOptions.length, initialTest]);

  useEffect(() => {
    if (!isOpen || !initialTest) return;

    if (initialTest.scheduled_month_year) {
      const [year, month] = initialTest.scheduled_month_year.split('-').map(Number);
      if (year && month) {
        setSelectedDate(new Date(year, month - 1, 1));
        setIsMonthOnly(true);
      } else {
        setSelectedDate(null);
        setIsMonthOnly(false);
      }
    } else if (initialTest.scheduled_date) {
      setSelectedDate(new Date(initialTest.scheduled_date));
      setIsMonthOnly(false);
    } else {
      setSelectedDate(null);
      setIsMonthOnly(false);
    }
    setNotes(initialTest.notes || '');

    const modalityKey = normalizeFutureTestModalityKey(initialTest.modality);
    const modalityMatch = FUTURE_TEST_MODALITY_OPTIONS.find(
      (option) => option.key === modalityKey
    );
    setSelectedModality(modalityMatch || null);

    const panelIds = initialTest.panel_ids?.length
      ? initialTest.panel_ids
      : initialTest.panel_id
        ? [initialTest.panel_id]
        : [];

    if (panelIds.length) {
      const matchedPanels = panelIds
        .map((panelId) => {
          const fromOptions = panelOptions.find(
            (option) => Number(option.key) === Number(panelId)
          );
          if (fromOptions) return fromOptions;

          const fromResponse = initialTest.panels?.find(
            (panel) => Number(panel.id) === Number(panelId)
          );
          if (fromResponse) {
            return { key: fromResponse.id, value: fromResponse.name || '' };
          }

          if (initialTest.panel && Number(initialTest.panel.id) === Number(panelId)) {
            return { key: initialTest.panel.id, value: initialTest.panel.name || '' };
          }

          return null;
        })
        .filter(Boolean) as SelectOption[];

      setSelectedPanels(matchedPanels);
    } else {
      setSelectedPanels([]);
    }

    if (initialTest.body_parts?.length) {
      const matchedBodyParts = initialTest.body_parts
        .map((bodyPart) => {
          const match = MRI_BODY_PART_OPTIONS.find(
            (option) => option.key === bodyPart || option.value === bodyPart
          );
          return match || { key: bodyPart, value: bodyPart };
        });
      setSelectedBodyParts(matchedBodyParts);
    } else {
      setSelectedBodyParts([]);
    }
  }, [isOpen, initialTest, panelOptions]);

  const isValid = useMemo(() => {
    if (!selectedDate || !selectedModality) return false;
    if (isBloodsSelected && selectedPanels.length === 0) return false;
    if (isMriSelected && selectedBodyParts.length === 0) return false;
    return true;
  }, [
    selectedDate,
    selectedModality,
    isBloodsSelected,
    isMriSelected,
    selectedPanels.length,
    selectedBodyParts.length
  ]);

  const isSubmitDisabled = !isValid || isSubmitting;

  const handleModalityChange = (option: SelectOption, _name: string) => {
    setSelectedModality(option);
    if (option.key !== BLOODS_MODALITY) {
      setSelectedPanels([]);
    }
    if (option.key !== MRI_MODALITY) {
      setSelectedBodyParts([]);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsTouched(true);

    if (!selectedDate || !selectedModality) return;
    if (isBloodsSelected && selectedPanels.length === 0) return;
    if (isMriSelected && selectedBodyParts.length === 0) return;
    if (!memberId) {
      toast.error('Member is required to create a future test');
      return;
    }

    setIsSubmitting(true);
    try {
      let scheduledDate: string | null = null;
      let scheduledMonthYear: string | null = null;

      if (isMonthOnly) {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        scheduledMonthYear = `${year}-${month}`;
      } else {
        const utcDate = new Date(
          selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000
        );
        scheduledDate = utcDate.toISOString();
      }

      const apiPayload = {
        scheduled_date: scheduledDate,
        scheduled_month_year: scheduledMonthYear,
        modality: String(selectedModality.key),
        member_id: memberId,
        notes: notes.trim() || undefined,
        ...(isBloodsSelected
          ? { panel_ids: selectedPanels.map((panel) => Number(panel.key)) }
          : {}),
        ...(isMriSelected
          ? { body_parts: selectedBodyParts.map((part) => String(part.key)) }
          : {})
      };

      const result = initialTest?.id
        ? await updateFutureTest({ id: String(initialTest.id), ...apiPayload })
        : await createFutureTest(apiPayload);

      onSubmit?.({
        ...apiPayload,
        ...result,
        modality: result.modality ?? apiPayload.modality,
        scheduled_date: result.scheduled_date ?? apiPayload.scheduled_date,
        scheduled_month_year:
          result.scheduled_month_year ?? apiPayload.scheduled_month_year,
        panel_ids: result.panel_ids ?? apiPayload.panel_ids,
        panels:
          result.panels?.length
            ? result.panels
            : isBloodsSelected
              ? selectedPanels.map((panel) => ({
                  id: panel.key,
                  name: String(panel.value),
                }))
              : undefined,
        body_parts: result.body_parts ?? apiPayload.body_parts,
        notes: result.notes ?? apiPayload.notes,
      });
      toast.success(initialTest?.id ? 'Future test updated' : 'Future test saved');
      onClose();
    } catch (error) {
      toast.error('Unable to save future test. Please try again.');
      console.error('Failed to save future test', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Future Tests'
      width='560px'
    >
      <form className='future-test-form' onSubmit={handleSubmit}>
        <div className='form-row'>
          <div className='schedule-mode-radios' role='radiogroup' aria-label='Schedule type'>
            <label className='schedule-mode-option'>
              <input
                type='radio'
                name='schedule_mode'
                checked={!isMonthOnly}
                onChange={() => {
                  if (isMonthOnly) {
                    setIsMonthOnly(false);
                    setSelectedDate(null);
                  }
                }}
              />
              <span>Select date</span>
            </label>
            <label className='schedule-mode-option'>
              <input
                type='radio'
                name='schedule_mode'
                checked={isMonthOnly}
                onChange={() => {
                  if (!isMonthOnly) {
                    setIsMonthOnly(true);
                    setSelectedDate(null);
                  }
                }}
              />
              <span>Select month</span>
            </label>
          </div>
          <DatePickerComp
            title={isMonthOnly ? 'Month *' : 'Date *'}
            placeholder={isMonthOnly ? 'Select month' : 'Select date'}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            monthOnly={isMonthOnly}
            showMonthDropdown={true}
            showYearDropdown={true}
          />
          {isTouched && !selectedDate && (
            <div className='field-error'>
              {isMonthOnly ? 'Month is required' : 'Date is required'}
            </div>
          )}
        </div>

        <div className='form-row'>
          <Select
            name='modality'
            title='Modality *'
            placeholder='Select modality'
            options={FUTURE_TEST_MODALITY_OPTIONS}
            selectedOption={selectedModality}
            onChange={handleModalityChange}
            isError={isTouched && !selectedModality}
            errorMessage={isTouched && !selectedModality ? '' : undefined}
            preventAutoOpenOnLoad={true}
            useMenuPortal={true}
          />
          {isTouched && !selectedModality && (
            <div className='field-error'>Modality is required</div>
          )}
        </div>

        {isBloodsSelected && (
          <div className='form-row'>
            <MultiSelect
              name='panels'
              title='Panel *'
              placeholder='Select panels'
              options={panelOptions}
              selectedOptions={selectedPanels}
              onChange={(option) =>
                setSelectedPanels((prev) => toggleMultiSelectOption(prev, option))
              }
              isError={isTouched && selectedPanels.length === 0}
              useMenuPortal={true}
            />
            {isTouched && selectedPanels.length === 0 && (
              <div className='field-error'>At least one panel is required</div>
            )}
          </div>
        )}

        {isMriSelected && (
          <div className='form-row'>
            <MultiSelect
              name='body_parts'
              title='Body Parts *'
              placeholder='Select body parts'
              options={MRI_BODY_PART_OPTIONS}
              selectedOptions={selectedBodyParts}
              onChange={(option) =>
                setSelectedBodyParts((prev) => toggleMultiSelectOption(prev, option))
              }
              isError={isTouched && selectedBodyParts.length === 0}
              useMenuPortal={true}
            />
            {isTouched && selectedBodyParts.length === 0 && (
              <div className='field-error'>At least one body part is required</div>
            )}
          </div>
        )}

        <div className='form-row'>
          <TextArea
            name='notes'
            title='Notes (optional)'
            placeholder='Add any notes for the care team'
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={4}
          />
        </div>

        <div className='form-actions'>
          <Button
            type='submit'
            text='Submit'
            isLoading={isSubmitting}
            isDisabled={isSubmitDisabled}
            width='100%'
          />
        </div>
      </form>
    </Modal>
  );
};

export default FutureTestsModal;
